import dayjs from "dayjs";
import { taskTypes, bookingStatus } from "@constants/booking";
import { vehicleTypes } from "@constants/vehicleInfo";
import { dumpSiteTypes } from "../../../../constants/dumpSites";
import { paymentTriggers } from "../../../../constants/payments";
import { bookingQuoteStatus } from "../../../../constants/booking";

const getFullName = (firstName, lastName = " ") => firstName?.trim() + " " + lastName?.trim();

export const formatESalesBookings = (bookings) => {
  const formatted = [];
  const formatter = (value, key) => {
    if (!value) return undefined;
    if (value === "true") return true;
    if (value === "false") return false;
    if (Number(value) && key !== "customer_phone") return Number(value);
    return value;
  };

  bookings?.forEach((b) => {
    const keys = Object.keys(b);
    const booking = {};
    keys.forEach((key) => {
      booking[key] = formatter(b[key]?.raw, key);
    });
    formatted.push(booking);
  });
  return formatted;
};

/* prod-all-tasks-2023-08-01 engine
accountManager::account_manager,
address,
adminActions__bookingCreated::is_admin_created,
amount_collected,
amount_charged,
amount_refunded,
approvalStatus::approval_status,
bundleId::bundle_id,
businessClient::is_business_client,
businessClientDetails__closedBy::closed_by,
companyName::company_name,
completeTaskSubmissionDate::date_warrior_completed,
completion_date::date_completed,
createdAt::created,
customer_id,
date::date_appointment,
description,
email::customer_email,
frequency,
fullName::customer_full_name,
guest_customer::is_guest_customer,
invoices,
isNonMarket::is_non_market,
isMultiDestination::is_multi_destination,
orderNumber::order_number,
paid,
payment_method,
paymentFrequency::payment_frequency,
paymentState::payment_state,
payout_dynamic::price_payout,
phone_number::customer_phone,
pricingZone::pricing_zone,
products,
recurringId::recurring_id,
refundState::refund_state,
rejectionsCount::rejections_count,
repeat::is_repeat,
sales_b2c::closed_by_b2c,
sales_cim::closed_by_cim,
sales_sdr::closed_by_sdr,
sales_type,
services,
subscriptionId::subscription_id,
status,
stripeCustomerId::stripe_customer_id,
suggestedDumpSitesCount::suggested_dumpsites_count,
task,
timeTracking__bookingPaidAt::date_paid,
total::price_total,
updatedAt::updated,
warrior_id,
warriorName::warrior_name,
warriorPayoutStatus::warrior_payout_status,
wasteTreatment::waste_treatment,
*/

const formatAddress = (address) => {
  const addressJson = address ? JSON.parse(address) : {};
  return {
    city: addressJson?.city,
    state: addressJson?.state,
  };
};

export const formatEBookings = (bookings) =>
  bookings?.map((b) => {
    const getTotalIn = () => {
      // total + customer rebate
      return parseInt(b.price_total?.raw ?? 0) + parseInt(b.rebate?.raw ?? 0);
    };

    const getTotalOut = () => {
      // warrior payout + loader payout + dumpsite payout + rebate_in
      return (
        parseInt(b.price_payout?.raw ?? 0) +
        parseInt(b.payout_loader_warrior?.raw ?? 0) +
        parseInt(b.payout_dumpsite_warrior?.raw ?? 0) +
        parseInt(b.rebate_customer?.raw ?? 0)
      );
    };

    return {
      accountManager: b.account_manager?.raw,
      additional_charges: parseInt(b.additional_charges?.raw ?? 0),
      amount_charged: parseInt(b.amount_charged?.raw ?? 0),
      amount_collected: parseInt(b.amount_collected?.raw ?? 0),
      amount_refunded: parseInt(b.amount_refunded?.raw ?? 0),
      assignedDumpsiteId: b.assigned_dumpsite?.raw,
      chargedState: b.charged_state?.raw,
      city: b.address?.raw ? formatAddress(b.address?.raw)?.city : "",
      state: b.address?.raw ? formatAddress(b.address?.raw)?.state : "",
      clean_total: parseInt(b.clean_total?.raw ?? 0),
      clientPaid: b.paid?.raw === "true",
      clientPaidDate: b.date_paid?.raw,
      closedBy: b.closed_by?.raw ?? "",
      completion_date: b.date_completed?.raw,
      companyName: b.company_name?.raw,
      createdAt: b.created?.raw,
      customer_id: b.customer_id?.raw,
      date: b.date_appointment?.raw,
      time: b.time?.raw,
      date_warrior_completed: b.date_warrior_completed?.raw,
      description: b.description?.raw,
      email: b.customer_email?.raw,
      frequency: b.frequency?.raw,
      fullName: b.customer_full_name?.raw,
      guest_customer: b.is_guest_customer?.raw === "true",
      hasConnectedPayment:
        b.invoices?.raw?.length > 0 || !!b.invoice?.raw || !!b.stripe_payment_id?.raw || parseInt(b.amount_charged?.raw ?? 0) !== 0,
      id: b.id?.raw,
      isMonthlyInvoiced: b.payment_frequency?.raw === paymentTriggers.monthly.value,
      location: b.location?.raw,
      loadType: b.load_type?.raw,
      orderNumber: b.order_number?.raw,
      payment_id: b.stripe_payment_id?.raw,
      payment_invoices: b.invoice?.raw ?? b.invoices?.raw ?? [],
      payment_method: b.payment_method?.raw,
      paymentState: b.payment_state?.raw,
      payout: b.price_payout?.raw || 0,
      phone: b.customer_phone?.raw,
      pricingZone: b.pricing_zone?.raw,
      pseudoId: b.order_number?.raw ?? b?.id?.raw,
      rebate_in: b.rebate?.raw ?? 0,
      rebate_out: b.rebate_customer?.raw ?? 0,
      recurringId: b.recurring_id?.raw,
      rejectionsCount: b.rejections_count?.raw,
      refundState: b.refund_state?.raw,
      sales_b2c: b.closed_by_b2c?.raw ?? "",
      sales_cim: b.closed_by_cim?.raw ?? "",
      sales_sdr: b.closed_by_sdr?.raw ?? "",
      sales_type: b.sales_type?.raw ?? "inbound",
      status: b.status?.raw,
      stripeCustomerId: b.stripe_customer_id?.raw,
      suggestedDumpSitesCount: b.suggested_dumpsites_count?.raw,
      task: b.task?.raw,
      total: parseInt(b.price_total?.raw ?? 0),
      warriorPayoutStatus: b.warrior_payout_status?.raw,
      wasteTreatment: b.waste_treatment?.raw[0],
      isMultiDestination: b.is_multi_destination?.raw,
      campaign: b.campaign?.raw ?? "",
      service_agreement: b.service_agreement?.raw ?? "",
      pandadoc_uploaded: b.service_agreement?.raw ? true : false,
      warrior_id: b.warrior_id?.raw,
      warriorName: b.warrior_name?.raw,
      total_in: getTotalIn(),
      total_out: getTotalOut(),

      // Loader warrior
      warrior_loader_id: b.warrior_loader_id?.raw,
      loaderWarriorName: b.loader_warrior_name?.raw,
      payout_loader_warrior: parseInt(b.payout_loader_warrior?.raw ?? 0),

      // Dumpsite warrior
      warrior_dumpsite_id: b.warrior_dumpsite_id?.raw,
      dumpsiteWarriorName: b.dumpsite_warrior_name?.raw,
      payout_dumpsite_warrior: parseInt(b.payout_dumpsite_warrior?.raw ?? 0),
    };
  }) || [];

export const formatBookings = (bookings) =>
  bookings?.map((b) => {
    const getTotalIn = () => {
      // total + customer rebate
      return parseInt(b.total ?? 0) + parseInt(b.rebate ?? 0);
    };

    const getTotalOut = () => {
      // warrior payout + loader payout + dumpsite payout + rebate_in
      return (
        parseInt(b.payout_dynamic ?? 0) +
        parseInt(b.payout_loader_warrior ?? 0) +
        parseInt(b.payout_dumpsite_warrior ?? 0) +
        parseInt(b.rebateCustomer ?? 0)
      );
    };

    return {
      id: b.id,
      companyName: b.companyName,
      orderNumber: b.orderNumber,
      associations: b.associations ?? [],
      pseudoId: b.orderNumber ?? b.id, // actual backend id
      fullName: getFullName(b.first_name, b.last_name),
      customer_id: b.customer_id,
      email: b.email,
      description: b.description,
      lat: b.address.lat,
      lng: b.address.lng,
      county: b.address.county,
      city: b.address.city,
      state: b.address.state,
      date: b.date,
      time: b.time,
      task: b.task,
      stripeCustomerId: b.stripeCustomerId,
      warriorPayoutStatus: b.warriorPayoutStatus,
      clientPaid: b.paid,
      completion_date: b.completion_date,
      date_warrior_completed: b.completeTaskSubmissionDate,
      status: b.status,
      rebate_in: b.rebate ?? 0,
      rebate_out: b.rebateCustomer ?? 0,
      pricingZone: b.pricingZone,
      createdAt: b.createdAt,
      assignedDumpsiteId: b.assignedDumpsiteId,
      total: parseInt(b.total ?? 0),
      payout: parseInt(b.payout_dynamic ?? 0),
      closedBy: b.businessClientDetails?.closedBy,
      sales_sdr: b.sales_sdr ?? "",
      sales_b2c: b.sales_b2c ?? "",
      sales_type: b.sales_type ?? "inbound",
      location: b.address.location,
      loadType: b.load_type,
      payment_method: b.payment_method,
      payment_id: b.stripePaymentId,
      payment_invoices: b.invoice ?? b.invoices ?? [],
      hasConnectedPayment: b.invoices?.length > 0 || !!b.invoice || !!b.stripePaymentId,
      amount_charged: b.amount_charged ?? 0,
      amount_collected: b.amount_collected ?? 0,
      amount_refunded: b.amount_refunded ?? 0,
      additional_charges: b.additional_charges ?? 0,
      isMonthlyInvoiced: b.paymentFrequency === paymentTriggers.monthly.value,
      paymentState: b.paymentState,
      chargedState: b.chargedState,
      refundState: b.refundState,
      accountManager: b.accountManager,
      rejectionsCount: b.rejectionsCount,
      wasteTreatment: b.wasteTreatment,
      guest_customer: b.guest_customer,
      isMultiDestination: b.isMultiDestination,
      frequency: b.frequency,
      campaign: b.campaign ?? "",
      service_agreement: b.service_agreement ?? "",
      pandadoc_uploaded: b.service_agreement ? true : false,
      warrior_id: b.warrior_id,
      warriorName: b.warriorName,
      total_in: getTotalIn(),
      total_out: getTotalOut(),

      // Loader warrior
      warrior_loader_id: b.warrior_loader_id,
      loaderWarriorName: b.loaderWarriorName,
      payout_loader_warrior: parseInt(b.payout_loader_warrior ?? 0),

      // Dumpsite warrior
      warrior_dumpsite_id: b.warrior_dumpsite_id,
      dumpsiteWarriorName: b.dumpsiteWarriorName,
      payout_dumpsite_warrior: parseInt(b.payout_dumpsite_warrior ?? 0),
    };
  }) || [];

export const formatEUsers = (users) =>
  users?.map((u) => ({
    id: u.id?.raw,
    firstName: u.firstname?.raw,
    lastName: u.lastname?.raw,
    fullName: u.fullname?.raw,
    company: u.company?.raw,
    companyId: u.company_id?.raw,
    email: u.email?.raw,
    city: u.city?.raw,
    phoneNumber: u.phone?.raw,
    tasksCount: u.taskcount?.raw,
    fiveStars: u.fivestars?.raw,
    penaltyPoints: u.penalty?.raw,
    penaltyAmount: u.penaltyAmount?.raw,
    isBanned: u.is_banned?.raw === "true",
    stripeUserId: u.stripeid?.raw,
    userType: u.usertype?.raw,
    lastJobDate: u.lastjobdate?.raw,
    gclId: u.gclid?.raw,
    backgroundClearanceDate: u.background_clearance_date?.raw,
    bookingCount: u.booking_count?.raw,
    createdAt: u.created?.raw,
    approvalState: u.approval_state?.raw,
    stripeConnected: u.stripe_connected?.raw && u.stripe_connected?.raw === "true",
    isMonthlyInvoiced: u.payment_frequency?.raw === paymentTriggers.monthly.value,
    tier: u.tier?.raw,
    disableSms: u?.disable_sms?.raw === "true",
    agreement: u?.agreement?.raw === "true",
    expectedPaymentTerms: u?.expected_payment_terms?.raw,
    expectedBookingFrequency: u?.expected_booking_frequency?.raw,
    accountManager: u?.account_manager?.raw,
    salesType: u?.sales_type?.raw,
    leadSource: u?.lead_source?.raw,
    discount: u?.discount?.raw,
    approvedVendor: u?.approvedvendor?.raw,
    preferredVendor: u?.preferredvendor?.raw,
    dumpSiteId: u?.dumpsiteid?.raw,
  })) || [];

export const formatEGuestUsers = (users) =>
  users.map((u) => ({
    id: u.id?.raw,
    firstName: u.firstname?.raw,
    lastName: u.lastname?.raw,
    fullName: u.full_name?.raw,
    phone: u.phone?.raw,
    email: u.email?.raw,
    company: u.company?.raw,
    createdAt: u.created?.raw,
    closedBy: u.closed_by?.raw,
    accountManager: u.account_manager?.raw,
    salesB2C: u.closed_by_b2c?.raw,
    salesSDR: u.closed_by_sdr?.raw,
    salesCIM: u.closed_by_cim?.raw,
  })) || [];

export const formatGuestUsers = (users) =>
  users?.map((u) => ({
    id: u.id,
    firstName: u.first_name,
    lastName: u.last_name,
    fullName: u.fullName,
    company: u.companyName,
    companyId: u.businessId,
    email: u.email,
    phone: u.phone,
    tasksCount: u.tasksCount,
    fiveStars: u.fiveStars,
    penaltyPoints: u.penaltyPoints,
    isBanned: u.isBanned,
    stripeUserId: u.stripeUserId,
    stripeCustomerId: u.stripeCustomerId || null,
    userType: u.userType,
    lastJobDate: u.lastJobDate,
    gclId: u.gclid,
    backgroundClearanceDate: u.backgroundClearanceDate,
    bookingCount: u.bookingCount,
    approvalState: u.approvalState,
    driversLicense: u.driversLicense,
    createdAt: u.createdAt,
    isMonthlyInvoiced: u.paymentFrequency === paymentTriggers.monthly.value,
    tier: u.tier ?? "tier4",
    disableSms: u?.disableSms,
    agreement: u?.providerAgreement?.readAndAgree,
    expectedPaymentTerms: u?.expectedPaymentTerms,
    expectedBookingFrequency: u?.expectedBookingFrequency,
    accountManager: u?.accountManager,
    salesType: u?.salesType,
    leadSource: u?.leadSource,
    discount: u?.discount,
    approvedVendor: u?.approvedVendor,
    preferredVendor: u?.preferredVendor,
    closedBy: u?.closedBy ?? null,
    salesB2C: u?.salesB2C ?? null,
    salesSDR: u?.salesSDR ?? null,
    salesCIM: u?.salesCIM ?? null,
  })) || [];

export const formatUsers = (users) =>
  users?.map((u) => ({
    id: u.id,
    firstName: u.firstName,
    lastName: u.lastName,
    fullName: u.fullName,
    company: u.companyName,
    companyId: u.businessId,
    email: u.email,
    phoneNumber: u.phoneNumber,
    tasksCount: u.tasksCount,
    fiveStars: u.fiveStars,
    penaltyPoints: u.penaltyPoints,
    penaltyAmount: u.penaltyAmount,
    isBanned: u.isBanned,
    stripeUserId: u.stripeUserId,
    stripeCustomerId: u.stripeCustomerId || null,
    userType: u.userType,
    lastJobDate: u.lastJobDate,
    gclId: u.gclid,
    backgroundClearanceDate: u.backgroundClearanceDate,
    bookingCount: u.bookingCount,
    approvalState: u.approvalState,
    driversLicense: u.driversLicense,
    createdAt: u.createdAt,
    isMonthlyInvoiced: u.paymentFrequency === paymentTriggers.monthly.value,
    tier: u.tier ?? "tier4",
    disableSms: u?.disableSms,
    agreement: u?.providerAgreement?.readAndAgree,
    expectedPaymentTerms: u?.expectedPaymentTerms,
    expectedBookingFrequency: u?.expectedBookingFrequency,
    accountManager: u?.accountManager,
    salesType: u?.salesType,
    leadSource: u?.leadSource,
    discount: u?.discount,
    approvedVendor: u?.approvedVendor,
    preferredVendor: u?.preferredVendor,
    dumpSiteId: u?.dumpSiteId,
  })) || [];

export const formatWarriors = (warriors) =>
  warriors?.map((warrior) => ({
    id: warrior.uid,
    bubbleId: warrior.bubbleId,
    email: warrior.email,
    fullName: warrior.fullName,
    selectedVehicles: warrior.vehicleInfo?.selectedVehicles?.map((v) => vehicleTypes[v.value]?.label),
    isHelperAvailable: !!warrior.vehicleInfo?.isHelperAvailable,
    services: warrior.savedPreferences?.services?.map((s) => taskTypes[s]?.label),
    isActive: !warrior.isBanned && warrior.lastCompletedJobDate && dayjs(warrior.lastCompletedJobDate).isAfter(dayjs().subtract(1, "month")),
    stripeId: warrior?.stripeCustomerId,
    data: warrior,
  })) || [];

export const formatEBusinessAccounts = (list) =>
  list?.map((u) => ({
    id: u.id?.raw,
    name: u.name?.raw,
    email: u.email?.raw,
    created_at: u.created_at?.raw,
    closed_by: u.closed_by?.raw,
  })) || [];

export const formatQuotes = (quotes) =>
  quotes.map((quote) => ({
    id: quote.id,
    fullName: quote.customerName,
    email: quote.customerEmail,
    description: quote.jobDescription,
    serviceType: taskTypes[quote.serviceType]?.label,
    phone: quote.phone,
    date: quote.date,
    data: quote,
    price: quote.total,
    quoteNumber: quote.quoteNumber,
    closedBy: quote.closedBy,
  }));

export const formatBids = (bids) =>
  bids.map((bid) => ({
    id: bid.id,
    bidNumber: bid.bidNumber,
    bidCount: bid.metaBids,
    bidLengthValue: bid.bidLengthValue,
    bidLengthType: bid.bidLengthType,
    status: bid.status,
    fullName: bid.fullName,
    email: bid.email,
    phone_number: bid.phone_number,
    companyName: bid.businessClientDetails?.name ?? bid.companyName,
    closedBy: bid.businessClientDetails?.closedBy ? bid.businessClientDetails?.closedBy : bid.isInbound ? "Self Serve Inbound" : null,
    sales_sdr: bid.sales_sdr,
    sales_b2c: bid.sales_b2c,
    companyId: bid.businessClientDetails?.id,
    businessClient: bid.businessClient,
    warriorPublicLink: bid.bookingBidWarriorUrl,
    createdAt: bid.createdAt,
    customer_id: bid.customer_id,
    expiresAt: bid.expiresAt,
    serviceType: bid.task,
    date: bid.date,
    timeQuoteCreated: bid.createdAt,
    bookingId: bid.bookingId,
    bookingQuoteId: bid.bookingQuoteId,
    meta: bid.meta,
    pricingZone: bid.pricingZone,
    isInbound: bid.isInbound,
    isAutoSelectOn: bid.isAutoSelectOn,
    sourceReferral: bid.sourceReferral,
    bookingDetails: bid.bookingDetails,
    bookingBidWarriorUrl: bid.bookingBidWarriorUrl,
  }));

export const formatRecurringBooking = (bookings) =>
  bookings?.map((item) => ({
    id: item.id,
    bubbleId: item.bubbleId,
    fullName: getFullName(item.first_name, item.last_name),
    customer_id: item.customer_id,
    warrior_id: item.warrior_id,
    warriorName: item.warriorName,
    email: item.email,
    address: item.address,
    serviceType: taskTypes[item.task]?.label,
    stripeId: item.stripeCustomerId,
    paid: item.total / 100,
    nextDateTime: item.time,
    nextDate: dayjs(item.date).tz(item.timeZone).format("MMM DD, YYYY"),
    status: bookingStatus.cancelled.value === bookingStatus[item.status]?.value ? bookingStatus[item.status].label : "Active",
    closedBy: item?.businessClientDetails?.closedBy ?? "-",
    cancelledBy: item.cancelledBy || "N/A",
    data: item,
    companyName: item.companyName,
    includeInvoice: item.includeInvoice ?? false,
    frequency: item?.frequency,
    frequencyPattern: item?.frequencyPattern ?? [],
    isMappedToSubscription: item?.isMappedToSubscription ?? false,
    hasSubscription: !!item.subscriptionId,
    accountManager: item?.accountManager,
    firstRecurringDate: item.firstRecurringDate ? dayjs(item.firstRecurringDate).tz(item.timeZone).format("MMM DD, YYYY") : "-",
  })) || [];

export const formatLocations = (locations) =>
  locations?.map((loc) => ({
    fullName: loc.name,
    id: loc.uid,
    data: loc,
  })) || [];

export const formatInvoices = (invoices) =>
  invoices?.map((invoice) => ({
    id: invoice.id,
    pseudoId: invoice.order_number,
    email: invoice.customer_email,
    paid: invoice.paid,
    total: invoice.total,
    due_date: dayjs(invoice.due_date),
    status: invoice.status,
    data: invoice,
  }));

export const formatDumpsites = (dumpsites) =>
  dumpsites.map((dumpsite) => ({
    fullName: dumpsite?.name || dumpsite?.googleName || "",
    address: { location: dumpsite.googleFormattedAddress, city: dumpsite.city },
    serviceType: dumpSiteTypes?.[dumpsite.type]?.label ?? "",
    serviceLocation: dumpsite?.serviceLocation ?? null,
    data: dumpsite,
  }));

export const formatBookingQuotes = (bookingQuotes) =>
  bookingQuotes.map((booking) => ({
    id: booking.id,
    fullName: getFullName(booking.first_name, booking.last_name),
    email: booking.email,
    description: booking.description,
    address: booking.address,
    dueDate: dayjs(booking.date).tz(booking.timeZone),
    serviceType: taskTypes[booking.task]?.value,
    status: bookingQuoteStatus[booking.status]?.value,
    pricingLocation: booking.pricingZone,
    data: booking,
    quoteNumber: booking.quoteNumber,
    bookingNumber: booking.bookingNumber,
    closedBy: booking.closedBy,
    customer_id: booking.customer_id,
    createdAt: booking.createdAt,
    isInbound: booking.isInbound,
  }));

export const formatBusinessAccounts = (businessAccounts) =>
  businessAccounts.map((businessAccount) => ({
    id: businessAccount.id,
    name: businessAccount.name,
    email: businessAccount.ownerEmail,
    closedBy: businessAccount.closedBy,
    data: businessAccount,
  }));

export const formatCoupons = (coupons) => {
  return coupons.map((coupon) => ({
    ...coupon,
    isArchived: Boolean(coupon.archived),
    isExpired: coupon.expiresAt ? dayjs(coupon.expiresAt).isBefore(dayjs()) : false,
    data: coupon,
  }));
};

export const slug = (string) => {
  if (!string) return "";
  return string?.toLowerCase()?.trim()?.split(" ")?.join("-")?.trim();
};

export const formatTaskTypes = (taskAvailable, taskSelected, taskOrbid = "task") => {
  return taskAvailable?.map((task) => {
    const label =
      taskOrbid === "bids" && task === "solar-panel-sell"
        ? "Material Resale"
        : task
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

    return {
      label,
      value: taskSelected.includes(task),
      id: task,
    };
  });
};

export const formatTasks = (tasks) => {
  return tasks.reduce((acc, task) => {
    acc[task] = {
      label: task
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      value: task,
    };
    return acc;
  }, {});
};
